import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import HelpDialog from '@/components/HelpDialog';
import { RotateCcw, Calendar, Trash2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { useSettings } from '@/contexts/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TASKS_PER_PAGE = 30;

export default function ArchivePage({ tasks, fetchTasks }) {
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [showEmptyDialog, setShowEmptyDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // holds task id to delete
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { energyTagsEnabled } = useSettings();

  // Fetch archived tasks with pagination. Intentionally NOT filtered by
  // context — tasks can be completed from the Active page, which shows
  // tasks across all contexts, so Archive must show them regardless of
  // which context happens to be globally selected.
  const fetchArchivedTasks = useCallback(async (skip = 0, append = false) => {
    setLoading(true);
    try {
      let url = `${API}/tasks?completed=true&skip=${skip}&limit=${TASKS_PER_PAGE}`;
      
      const response = await axios.get(url, { withCredentials: true });
      const data = response.data;
      
      if (data.tasks) {
        // Paginated response
        if (append) {
          setArchivedTasks(prev => [...prev, ...data.tasks]);
        } else {
          setArchivedTasks(data.tasks);
        }
        setHasMore(data.has_more);
        setTotalCount(data.total);
      } else {
        // Non-paginated response (fallback)
        const filtered = data
          .filter((t) => t.completed)
          .sort((a, b) => {
            const dateA = new Date(a.completed_at || a.created_at);
            const dateB = new Date(b.completed_at || b.created_at);
            return dateB - dateA;
          });
        setArchivedTasks(filtered);
        setHasMore(false);
        setTotalCount(filtered.length);
      }
    } catch (error) {
      console.error('Error fetching archived tasks:', error);
      toast.error('Failed to load archived tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchArchivedTasks(0, false);
  }, [fetchArchivedTasks]);

  const handleLoadMore = () => {
    fetchArchivedTasks(archivedTasks.length, true);
  };

  const handleRestore = async (taskId) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}`, {
        completed: false,
        completed_at: null,
      }, { withCredentials: true });
      toast.success('Task restored to storage');
      // Remove from local state and update counts
      setArchivedTasks(prev => prev.filter(t => t.id !== taskId));
      setTotalCount(prev => prev - 1);
      fetchTasks();
    } catch (error) {
      console.error('Error restoring task:', error);
      toast.error('Failed to restore task');
    }
  };

  const handleDeleteTask = async () => {
    if (!showDeleteConfirm) return;
    try {
      await axios.delete(`${API}/tasks/${showDeleteConfirm}`, {
        withCredentials: true
      });
      toast.success('Task deleted permanently');
      // Remove from local state and update counts
      setArchivedTasks(prev => prev.filter(t => t.id !== showDeleteConfirm));
      setTotalCount(prev => prev - 1);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleEmptyArchive = async () => {
    try {
      // Delete all archived tasks
      for (const task of archivedTasks) {
        await axios.delete(`${API}/tasks/${task.id}`, { withCredentials: true });
      }
      toast.success('Archive emptied successfully');
      setShowEmptyDialog(false);
      setArchivedTasks([]);
      setTotalCount(0);
      setHasMore(false);
    } catch (error) {
      console.error('Error emptying archive:', error);
      toast.error('Failed to empty archive');
    }
  };

  return (
    <main className="pb-6 sm:pb-8" data-testid="archive-page">
      <div className="sticky top-[60px] z-20 pt-2 md:pt-6 pb-4 mb-6 bg-slate-900">
        <header className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Desktop: Header row - title and button */}
          <div className="hidden md:block">
            <div className="flex items-center gap-2">
              <h1 className="h-font text-2xl md:text-3xl font-semibold text-white">Task Archive</h1>
              <HelpDialog 
                title="Task Archive Guide" 
                content={`**When a task is checked off as completed, it is automatically moved from Task Storage to the Archive.** From here, tasks can be "uncompleted" (moving them back to Storage) or deleted permanently. Tasks are **automatically deleted** from the Archive after 60 days (you can change this number in settings).`}
                size="large"
              />
              {totalCount > 0 && (
                <span className="text-sm text-muted-foreground">
                  ({archivedTasks.length} of {totalCount})
                </span>
              )}
            </div>
            
            {/* Action buttons row - below header, left-aligned */}
            {totalCount > 0 && (
              <div className="mt-3">
                <Button
                  onClick={() => setShowEmptyDialog(true)}
                  variant="secondary"
                  size="sm"
                  data-testid="empty-archive-button"
                  style={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    backgroundColor: 'var(--color-secondary)'
                  }}
                >
                  Empty Archive
                </Button>
              </div>
            )}
          </div>
          
          {/* Mobile: Header with Empty Archive button */}
          <div className="md:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h1 className="h-font text-2xl font-semibold text-white">Task Archive</h1>
                <HelpDialog 
                  title="Task Archive Guide" 
                  content={`**When a task is checked off as completed, it is automatically moved from Task Storage to the Archive.** From here, tasks can be "uncompleted" (moving them back to Storage) or deleted permanently. Tasks are **automatically deleted** from the Archive after 60 days (you can change this number in settings).`}
                  size="large"
                />
              </div>
              {totalCount > 0 && (
                <Button
                  onClick={() => setShowEmptyDialog(true)}
                  variant="secondary"
                  size="sm"
                  data-testid="empty-archive-button-mobile"
                  style={{
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    backgroundColor: 'var(--color-secondary)'
                  }}
                >
                  Empty Archive
                </Button>
              )}
            </div>
          </div>
        </header>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-6">
        {archivedTasks.length === 0 && !loading && (
        <div
          className="text-center py-16 px-4 rounded-2xl"
          style={{ backgroundColor: 'var(--color-secondary)' }}
          data-testid="empty-archive-state"
        >
          <p className="text-lg text-muted-foreground">No archived tasks yet</p>
          <p className="text-sm text-muted-foreground mt-2">
            Completed tasks will appear here
          </p>
        </div>
      )}

      <div className="space-y-3">
        {archivedTasks.map((task, i) => (
          <motion.div
            key={task.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i, 10) * 0.02 }}
            className="bg-card text-card-foreground rounded-2xl border border-[hsl(var(--border))] shadow-[0_8px_24px_rgba(0,0,0,0.06)] px-6 py-3 md:py-4 hover:shadow-[0_12px_28px_rgba(0,0,0,0.08)] transition-shadow"
            data-testid="archived-task-card"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm md:text-base leading-snug line-through text-muted-foreground">
                  {task.title}
                </h3>
                {task.notes && (
                  <p className="text-xs md:text-sm text-muted-foreground mt-1 line-clamp-2">
                    {task.notes}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  {task.tag_type && (
                    <Badge variant="secondary" className="text-xs opacity-60" style={{ backgroundColor: 'var(--color-secondary)', color: 'black' }}>
                      {task.tag_type}
                    </Badge>
                  )}
                  {task.tag_domain && (
                    <Badge variant="secondary" className="text-xs opacity-60" style={{ backgroundColor: 'var(--color-secondary)', color: 'black' }}>
                      {task.tag_domain}
                    </Badge>
                  )}
                  {energyTagsEnabled && task.tag_energy && (
                    <Badge variant="secondary" className="text-xs opacity-60" style={{ backgroundColor: 'var(--color-secondary)', color: 'black' }}>
                      {task.tag_energy}
                    </Badge>
                  )}
                </div>
                {task.completed_at && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                    <Calendar size={14} />
                    <span>
                      Completed: {format(new Date(task.completed_at), 'MMM d, yyyy')}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRestore(task.id)}
                  aria-label="Restore task"
                  data-testid="restore-task-button"
                  title="Uncomplete Task"
                >
                  <RotateCcw size={18} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowDeleteConfirm(task.id)}
                  aria-label="Delete task"
                  data-testid="delete-task-button"
                  className="text-destructive hover:text-destructive"
                  title="Permanently Delete Task"
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
        
        {/* Load More Button */}
        {hasMore && (
          <Button
            onClick={handleLoadMore}
            disabled={loading}
            variant="outline"
            className="w-full"
            data-testid="load-more-archive"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              `Load More (${totalCount - archivedTasks.length} remaining)`
            )}
          </Button>
        )}
      </div>

      {/* Empty Archive Confirmation Dialog */}
      <Dialog open={showEmptyDialog} onOpenChange={setShowEmptyDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Empty Archive</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to permanently delete all {totalCount} archived task{totalCount !== 1 ? 's' : ''}? This action cannot be undone.
          </p>
          <DialogFooter className="gap-3 sm:space-x-0">
            <Button variant="outline" onClick={() => setShowEmptyDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleEmptyArchive}>
              Empty Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Task Confirmation Dialog */}
      <AlertDialog open={!!showDeleteConfirm} onOpenChange={(open) => !open && setShowDeleteConfirm(null)}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3 sm:space-x-0">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTask}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </main>
  );
}
